import { FunctionComponent } from 'react'
import styled from 'styled-components'
import AnalyzeFeedback from '@/components/kol/analyze-feedback'
import KolDetailDataBlock from '@/components/kol/common/kol-detail-data-block'
import AudienceAgeGender from '@/components/kol/detail/audience-analyses/audience-age-gender'
import AudienceLocation from '@/components/kol/detail/audience-analyses/audience-location'
import { useIntl } from '@/i18n/hooks/use-intl'
import { Audience } from '@/store/kol-detail'
import { FeedbackType } from '@/types/api/feedback'
import { KolSchemaModel } from '@/types/schema/kol-schema'
import { ampli, ClickReportKolAudienceAccuracyProperties } from '@/utils/ampli'
import { Platform, PlatformCode } from '@/utils/convert-platform'

interface AudienceAnalysisProps {
  audienceData?: Audience
  kolInfo?: KolSchemaModel
  isFetchingKolInfo: boolean
  isFetchingAudienceAnalyze: boolean
  platform: Platform
  isLocked: boolean
  showMockData?: boolean
  isPublicDetailPage: boolean
}

const AudienceAnalysis: FunctionComponent<AudienceAnalysisProps> = ({
  audienceData,
  kolInfo,
  isFetchingKolInfo,
  isFetchingAudienceAnalyze,
  platform,
  isLocked,
  showMockData,
  isPublicDetailPage,
}) => {
  const { formatMessage } = useIntl()

  const enabledLocationData = [
    PlatformCode.Instagram,
    PlatformCode.YouTube,
  ].includes(platform.code)

  return (
    <KolDetailDataBlock
      hoverableExtraContent
      colSpan={24}
      header={{
        poweredByAI: true,
        title: formatMessage(
          { id: 'kol:audience_analysis_title' },
          { platform: platform.title },
        ),
        tooltip: {
          title: formatMessage({ id: 'kol:audience_analysis_data_source' }),
        },
        extraContent: (
          <AnalyzeFeedback
            feedbackEvent={(
              eventProps: ClickReportKolAudienceAccuracyProperties,
            ): void => {
              ampli.clickReportKolAudienceAccuracy(eventProps)
            }}
            feedbackType={FeedbackType.Audience}
            kolInfo={kolInfo}
            platform={platform.shortcode}
          />
        ),
      }}
      kolId={kolInfo?.id}
      kolMetrics='Audience'
      kolName={kolInfo?.name}
      platform={platform.shortcode}
    >
      <Box>
        <AudienceAgeGender
          audienceData={audienceData}
          enabledLocationData={enabledLocationData}
          isFetchingAudienceAnalyze={isFetchingAudienceAnalyze}
          isFetchingKolInfo={isFetchingKolInfo}
          isLocked={isLocked}
          isPublicDetailPage={isPublicDetailPage}
          kolInfo={kolInfo}
          platform={platform}
          showMockData={showMockData}
        />
        {enabledLocationData && (
          <AudienceLocation
            audienceData={audienceData}
            isFetching={isFetchingAudienceAnalyze}
            isLocked={isLocked}
            isPublicDetailPage={isPublicDetailPage}
            platform={platform}
            showMockData={showMockData}
          />
        )}
      </Box>
    </KolDetailDataBlock>
  )
}

export default AudienceAnalysis

const Box = styled.div`
  display: flex;
  gap: 30px;
  flex-direction: column;

  @media (min-width: 981px) {
    flex-direction: row;
  }

  > div {
    flex-basis: 100%;
  }
`

export const StatisticWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(24, minmax(0, 1fr));

  > * {
    grid-column: span 8;
  }
`

export const Percentage = styled.div`
  color: ${({ theme }): string => theme.colors.brand.secondary};
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  align-self: flex-end;
  line-height: 140%;
`
